/**
 * Global keyboard event handling.
 * Captures keydown events and forwards them to .NET for shortcut processing.
 */

'use strict';

import type { DotNetObjectReference, KeyboardPayload } from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

/**
 * Internal state for keyboard functionality.
 */
interface KeyboardState {
    dotnet: DotNetObjectReference | null;
    handler: ((e: KeyboardEvent) => void) | null;
    enabled: boolean;
}

const state: KeyboardState = {
    dotnet: null,
    handler: null,
    enabled: false
};

/**
 * Event listener options for capturing keyboard events.
 */
const CAPTURE_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true };

/**
 * Extract keyboard event information into a payload for .NET.
 * 
 * @param event - Keyboard event
 * @returns Payload with key information and modifiers
 */
function createKeyboardPayload(event: KeyboardEvent): KeyboardPayload {
    return {
        key: event.key,
        code: event.code,
        ctrl: !!event.ctrlKey,
        shift: !!event.shiftKey,
        alt: !!event.altKey,
        meta: !!event.metaKey
    };
}

/**
 * Initialize global keydown listener (capturing phase).
 * 
 * @param dotnetHelper - .NET object reference for callbacks
 * @returns True if successfully initialized
 */
export function initialize(dotnetHelper: DotNetObjectReference): boolean {
    try {
        state.dotnet = dotnetHelper;

        // Already enabled, no need to re-attach
        if (state.enabled) {
            return true;
        }

        state.handler = (event: KeyboardEvent) => {
            const payload = createKeyboardPayload(event);
            safeInvokeSync(dotnetHelper, 'OnKeyDown', payload);
        };

        window.addEventListener('keydown', state.handler, CAPTURE_LISTENER_OPTIONS);
        state.enabled = true;

        return true;
    } catch {
        return false;
    }
}

/**
 * Dispose keyboard listener and clear state.
 */
export function dispose(): void {
    try {
        if (state.handler) {
            window.removeEventListener('keydown', state.handler, CAPTURE_LISTENER_OPTIONS);
        }
    } finally {
        state.enabled = false;
        state.handler = null;
        state.dotnet = null;
    }
}
